<template>
  <div class="section-container">
    <div class="section-header">
      {{ section.displayName }}
    </div>
    <div
      v-if="!blockWidth.xs"
      role="table"
      :aria-describedby="sectionId ? `section-summary-${sectionId}` : undefined"
      :aria-rowcount="rows.length + 1"
      :aria-colcount="enabledColumns.length"
      class="pricing-table"
    >
      <div role="rowgroup">
        <PricingTableColumnHeaders
          :style="gridStyle"
          :section="section"
          :is-pre-calculated="isPreCalculated"
          class="column-headers"
        />
      </div>
      <div role="rowgroup">
        <PricingTableRow
          v-for="(row, index) in rows"
          :key="row.id"
          :section="section"
          class="sub-element"
          :data-external="`pricing-table-row-index:${index}`"
          :merge-values="mergeValues"
          :fields="fields"
          :recipient-status="recipient?.status"
          :allowed-to-edit-after-partially-signed="allowedToEditAfterPartiallySigned"
          :recipient-input="recipientInput"
          :row="row"
          :columns="columns"
          :grid-style="gridStyle"
          :currency-settings="currencySettings"
          :is-pre-calculated="isPreCalculated"
          :index="index"
          @update-input-value="$emit('update-input-value', $event)"
        />
      </div>
    </div>
    <div v-if="blockWidth.xs" class="mobile-pricing-cards">
      <PricingTableRow
        v-for="(row, index) in rows"
        :key="row.id"
        :section="section"
        class="sub-element"
        :data-external="`pricing-table-row-index:${index}`"
        :merge-values="mergeValues"
        :fields="fields"
        :recipient-status="recipient?.status"
        :allowed-to-edit-after-partially-signed="allowedToEditAfterPartiallySigned"
        :recipient-input="recipientInput"
        :row="row"
        :columns="columns"
        :grid-style="gridStyle"
        :currency-settings="currencySettings"
        :is-pre-calculated="isPreCalculated"
        :index="index"
        @update-input-value="$emit('update-input-value', $event)"
      />
    </div>
    <div v-if="sectionSummary.price.enabled" class="section-footer viewer">
      <PricingTableSectionSummaryNew
        v-if="isSectionSummaryEnabled"
        :id="sectionId ? `section-summary-${sectionId}` : undefined"
        :pricing-table="pricingTable"
        :section-id="sectionId"
        :fields="fields"
        class="sub-element viewer"
      />
    </div>
  </div>
</template>
<script setup lang="ts">
import type {
  PricingTableSection as IPricingTableSection,
  PricingTableCurrencySettings,
  MergeValues,
  Field,
  PricingTable,
} from '@getaccept/editor-lib';
import {
  PricingTableHelper,
  useEditorMetaDataStore,
  usePricingTableColumns,
  usePricingTableRows,
  usePricingTableSectionSummary,
} from '@getaccept/editor-lib';
import type { EditorRecipientInput } from '@getaccept/lib-shared/src/types/editor-recipient-input';
import type { Recipient } from '@getaccept/lib-shared/src/types/Recipient';
import { computed, toRef } from 'vue';
import { storeToRefs } from 'pinia';
import { useInjectEditorViewState } from '../../composables/editor-view-state.composable';
import PricingTableColumnHeaders from './PricingTableColumnHeaders.vue';
import PricingTableRow from './PricingTableRow.vue';
import PricingTableSectionSummaryNew from './PricingTableSectionSummaryNew.vue';

const {
  pricingTable,
  section,
  sectionId = '',
  currencySettings,
  isPreCalculated,
  mergeValues,
  fields = [],
  recipientInput = [],
  recipient,
  allowedToEditAfterPartiallySigned,
} = defineProps<{
  pricingTable: PricingTable;
  section: IPricingTableSection;
  sectionId?: string;
  currencySettings?: PricingTableCurrencySettings;
  isPreCalculated?: boolean;
  mergeValues: MergeValues;
  fields?: Field[];
  recipientInput?: EditorRecipientInput[];
  recipient?: Recipient;
  allowedToEditAfterPartiallySigned?: boolean;
}>();

defineEmits<{
  'update-input-value': [{ id: string; value: string | undefined }];
}>();

const { productProperties } = useInjectEditorViewState();
const { blockWidth } = storeToRefs(useEditorMetaDataStore());

const { sectionSummary, isSectionSummaryEnabled } = usePricingTableSectionSummary(
  toRef(() => pricingTable),
  toRef(() => sectionId),
  toRef(() => fields),
  productProperties
);

const sectionRef = toRef(() => section);

const { rows } = usePricingTableRows(sectionRef);

const { columns, columnWidths, enabledColumns } = usePricingTableColumns(
  sectionRef,
  toRef(() => isPreCalculated),
  productProperties
);

const gridStyle = computed(() =>
  PricingTableHelper.getGridStyle(columnWidths.value.filter(width => width !== undefined))
);
</script>
<style lang="scss" scoped>
@import '@getaccept/editor-lib/src/scss/pricing-table-section';

.section-container {
  padding: var(--spacing-50);
  position: relative;

  .section-footer {
    justify-content: flex-end;
  }

  &::after {
    content: '';
    width: 100%;
    height: 100%;
    pointer-events: none;
    position: absolute;
    top: 0;
    left: 0;
    border: var(--editor-focus-outline) solid var(--blue-60);
    opacity: 0;
    transition: opacity 0.2s;
    border-radius: var(--editor-medium-border-radius);
  }

  &.hovered,
  &.selected {
    &::after {
      opacity: 1;
    }
  }
}
</style>
